import {
  AppointmentMeta,
  AppointmentModel
} from "@devexpress/dx-react-scheduler";
import { useFlags } from "launchdarkly-react-client-sdk";
import * as React from "react";
import {
  Suspense,
  startTransition,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useLocation } from "react-router-dom";

import { datadogRum } from "@datadog/browser-rum";
import {
  addMinutes,
  differenceInMinutes,
  endOfWeek,
  format,
  isEqual,
  isWithinInterval,
  startOfWeek
} from "date-fns";
import { useDebounce, useErrors } from "hooks";
import { omit } from "lodash";
import { v4 as uuidV4 } from "uuid";

import { useAppDispatch, useAppSelector } from "app/hooks";
import {
  selectSnackbarActionType,
  setActionType,
  setCustomMessage,
  setLabel,
  setOpen
} from "components/action-snackbar/action-snackbar-slice";
import { SNACKBAR_DURATION_FOREVER } from "components/action-snackbar/action-snackbar.component";
import {
  asksCancellationMessage,
  withCancellationMessageChoice
} from "components/cancellation-message-choice";
import {
  setOpen as setConfirmChangeDialogOpen,
  setContent,
  setOnCancel,
  setOnChange,
  setOptions,
  setTitle
} from "components/confirm-dialog/confirm-dialog.slice";
import { DialogLoadErrorBoundary } from "components/dialog-load-error-boundary/dialog-load-error-boundary.component";
import DialogLoadingFallback from "components/dialog-loading-fallback";
import ErrorStateSnackbarContent from "components/error-state-snackbar-content";
import WarningSnackbarContent from "components/warning-snackbar-content/warning-snackbar-content.component";
import {
  APPOINTMENT_STATUS_CANCELED,
  APPOINTMENT_STATUS_COMPLETED,
  APPOINTMENT_STATUS_DRAFT,
  APPOINTMENT_STATUS_NO_SHOW,
  EVENT_TYPE_APPOINTMENT_CODE,
  EVENT_TYPE_SCHEDULE_CODE,
  EVENT_TYPE_WORK_SHIFT_CODE,
  NON_FINAL_STATUSES_CODES,
  QUICK_FILTER_DEFAULT_TYPE,
  QUICK_FILTER_DRAFT_APPOINTMENTS,
  VISIBLE_STATUSES_CODES
} from "constants/appointments";
import {
  ALLOW_ENTERING_CANCELLATION_REASON,
  GLOBAL_PRACTICE_EVENTS_FEATURE,
  SCHEDULER_EVENTS_REFETCH_SCOPING,
  SCHEDULING_V2_FEATURE,
  SHOW_ALL_CLINICS,
  WEB_SCHEDULER_APPOINTMENTS_FILTER
} from "constants/feature-flags";
import {
  clearRecordMetric,
  selectCurrentClinic,
  selectCurrentPractice,
  selectCurrentRegion,
  selectCurrentUser,
  selectHasRegions,
  selectNumberOfPracticeClinics,
  selectRecordMetric,
  selectUserClinicsIds
} from "features/auth/auth-slice";
import { SelectAppointmentOutcomeProvider } from "hooks/use-appointment-outcome";
import { useDedicatedSlotEnforcement } from "hooks/use-dedicated-slot-enforcement.hook";
import {
  useIsOpportunity,
  useOpportunityFieldEnabled
} from "hooks/use-is-opportunity.hook";
import { ScheduleNextAppointmentProvider } from "hooks/use-schedule-next-appointment";
import { useStickyTrue } from "hooks/use-sticky-true.hook";
import { UndeliveredDevicesProvider } from "hooks/use-undelivered-devices-check";
import {
  useAddAppointmentMutation,
  useDeleteAppointmentMutation,
  useUpdateAppointmentMutation
} from "services/appointments";
import { useGetClinicsQuery } from "services/clinics";
import { EVENTS_POLL_SECONDS } from "services/events";
import {
  useAddScheduleMutation,
  useDeleteScheduleMutation,
  useUpdateScheduleMutation
} from "services/schedules";
import {
  Appointment,
  AppointmentRecurrenceInput,
  DeleteAppointmentInput
} from "services/types/appointments.type";
import { Clinic } from "services/types/clinics.type";
import { AppointmentBlock, PersonalEvent } from "services/types/events.type";
import {
  DeleteScheduleInput,
  Schedule,
  ScheduleInstanceInput
} from "services/types/schedules.type";
import { MinimalStaffMember, StaffMember } from "services/types/staff.type";
import { shouldCollectOutcome } from "utils/appointments.util";
import { isStaffMemberAvailableUtil } from "utils/build-staff-member-work-shift.util";
import {
  convertDateFromCurrentTimezoneToTimezone,
  parseBackendDateOnly
} from "utils/common";
import {
  DEDICATED_SLOT_RESERVED,
  dragReservedSlotWarning,
  slotAcceptsType,
  withNotMovedPrefix
} from "utils/dedicated-slots.util";
import {
  isAppointment,
  isAppointmentBlock,
  isAppointmentDeletable,
  isAppointmentEditable,
  isClinicEvent,
  isPersonalEvent,
  isPersonalWorkShiftEvent
} from "utils/events.util";
import { lazyWithRetry } from "utils/lazy-with-retry.util";
import { isManuallyFlagged } from "utils/opportunity.util";
import { safeRefetch } from "utils/services.util";

import { AppointmentFormValues } from "../appointment-form/appointment-form.component";
import ProviderConflictModal from "../appointment-form/provider-conflict-modal/provider-conflict-modal.component";
import {
  hasStaffBusyError,
  isStaffBusyError
} from "../appointment-form/provider-conflict-modal/provider-conflict-modal.util";
import {
  closeForm,
  hidePreview,
  openForm,
  selectAppointmentTypesFilterValues,
  selectFilterValues,
  selectFiltersReady,
  selectFormOpen,
  selectIncludeSupervisingProvider,
  selectIsShowByClinicViewMode,
  selectStaffFilterValues,
  setEditRecurringEventType,
  setSelectedFromAvailableStaff,
  setShowFromAllLocations
} from "../scheduler-slice";
import {
  ALL_EVENTS,
  CURRENT_AND_FOLLOWING,
  CURRENT_EVENT
} from "../scheduler/confirmation-dialog/recurring-events-confirmation/edit-recurring-event-confirmation.util";
import EditRecurringEventDialogContent from "../scheduler/confirmation-dialog/recurring-events-confirmation/edit-recurring-event-dialog-content.component";
import { ReservedAppointmentTypesProvider } from "../scheduler/use-reserved-appointment-types.hook";
import getAppointmentsBaseFilter from "../utils/get-appointments-base-filters.util";
import { getEventsData } from "../utils/get-events-data.util";
import { getFriendlyAppointmentSaveError } from "../utils/get-friendly-appointment-save-error.util";
import { resolveDraftConfirmationStatus } from "../utils/resolve-draft-confirmation-status.util";
import {
  calculateZIndex,
  findOverlappedAppointments,
  getClinicIdsFromEvents,
  sortAppointmentsByStartDate
} from "../utils/scheduling.utils";
import { stripRecurrenceCarryover } from "../utils/strip-recurrence-carryover.util";
import CloneEventDialogContent from "./components/clone-event-dialog-content.component";
import MoveEventDialogContent from "./components/move-event-dialog-content.component";
import useAppointmentCounters from "./use-appointments-counters.hook";
import useRoomValidation from "./use-appointments-room.hook";
import { useSchedulerEvents } from "./use-scheduler-events.hook";
import { getAppointmentPatientData } from "./utils/get-patient-data.util";
import { moveEventTitle } from "./utils/move-event-label.util";
import {
  isScheduleKind,
  scheduleMoveBody
} from "./utils/schedule-move-body.util";

const AppointmentForm = lazyWithRetry(
  () => import("../appointment-form/appointment-form.component"),
  "appointment-form"
);

// The full move payload, kept whole so a "Book anyway" resave is a pure replay of the
// drop the user already made — no re-deriving timezone/room offsets at confirm time.
type MovedEventInput = {
  staffMemberId: number;
  clinicId: number;
  id: number;
  type: string;
  startDate: Date;
  endDate: Date;
  timezone: string;
  modelId?: number | null;
  prevStartDate?: Date;
  room_start_moment?: string | null;
  room_end_moment?: string | null;
  allowOverlap?: boolean;
  // A clinic event's own staff, for its `staff_members` swap (ENG-2297): the column it
  // was dragged from, its people, and whether it is practice-wide.
  prevStaffMemberId?: number;
  staffMemberIds?: number[];
  isGlobalPracticeEvent?: boolean;
  // DevExpress reports `clinicId` only when the grid is grouped by clinic, so on an
  // ordinary same-clinic drag it is undefined. The move payload tolerates that (an
  // undefined clinic key is dropped and the server keeps the current one), but the
  // conflict modal cannot: busy-times uses the booking clinic to decide which rows may
  // carry a patient name, and without it every name comes back blank — including the
  // same-clinic ones the form path does show.
  bookingClinicId?: number;
};

// DevExpress reports `clinicId` in a drop payload only when the grid is grouped by
// clinic, so on an ordinary drag it is undefined and a bare `!==` reads as "the clinic
// changed" on EVERY drag. That silently killed the "nothing actually moved" early
// return below and made the confirm dialog render an empty clinic line (ENG-1848 QA).
export const isSameClinicDrop = (
  eventClinicId?: number,
  droppedClinicId?: number
) => droppedClinicId == null || eventClinicId === droppedClinicId;

const AppointmentsContext = React.createContext<any>(null);

type AppointmentsProviderProps = {
  children?: React.ReactNode;
  selectedClinics?: Clinic[];
  /**
   * Mount only to expose `setupAppointmentModal` + `appointmentFormId` (the
   * way patient routes already use this provider) and never fetch events or
   * work shifts. For hosts that live outside any scheduling surface — the quick
   * launcher — where a week-window `{*}` events query would be pure waste.
   */
  formOnly?: boolean;
};

export type ActionType = "add" | "update" | "delete";
export const EVENTS_QUERY = "{*}";

export function AppointmentsProvider({
  children,
  selectedClinics,
  formOnly = false
}: AppointmentsProviderProps) {
  const dispatch = useAppDispatch();
  const { isRoomAvailable } = useRoomValidation();
  const currentPractice = useAppSelector(selectCurrentPractice);
  const isShowByClinicView = useAppSelector(selectIsShowByClinicViewMode);
  const includeSupervisingProvider = useAppSelector(
    selectIncludeSupervisingProvider
  );
  const hasRegions = useAppSelector(selectHasRegions);
  const currentRegion = useAppSelector(selectCurrentRegion);
  const recordMetric = useAppSelector(selectRecordMetric);
  const numberOfPracticeClinics =
    useAppSelector(selectNumberOfPracticeClinics) || 0;
  const multiClinic = numberOfPracticeClinics > 1;
  const userClinicsIds = useAppSelector(selectUserClinicsIds);

  const [appointmentIdMutationInProgress, setAppointmentIdMutationInProgress] =
    useState<number | undefined>();

  const [editingForm, setEditingForm] = useState<boolean>(false);
  // NOT editingForm — several external callers (new-appointment-button,
  // find-next-available, rescheduling-modal, etc.) dispatch openForm directly
  // to Redux without ever touching editingForm; selectFormOpen is the one
  // state every opener and the form's own visibility both read.
  const isFormOpenInRedux = useAppSelector(selectFormOpen);
  const shouldMountAppointmentForm = useStickyTrue(isFormOpenInRedux);
  // One derived boolean gates the whole cross-clinic conflict UX, matching the form
  // view's own gate so the two paths can never skew (ENG-1831, ENG-1848).
  const isCrossClinicConflictGateOn = Boolean(
    currentPractice?.scheduling_cross_clinic_conflict_check
  );
  const { isEnforced: isDedicatedSlotEnforced } = useDedicatedSlotEnforcement();
  // ENG-1848: the move whose save was rejected for a provider conflict. Holding the
  // whole input (not just the window) makes "Book anyway" a replay rather than a
  // second, subtly different save.
  const [dragConflict, setDragConflict] = useState<MovedEventInput | null>(
    null
  );
  // Ref, not state: a fast double click lands before the close re-renders, and a state
  // read would still be stale — two replays would be two moves (ENG-1831 hit the same
  // thing on the form's own "Book anyway").
  const dragReplayFiredRef = useRef(false);
  const appointmentMetaRef = useRef<AppointmentMeta | undefined>();
  const [currentAppointment, setCurrentAppointment] = useState<
    Partial<AppointmentFormValues>
  >({});
  const [originalAppointment, setOriginalAppointment] = useState<
    Partial<Appointment> | Partial<PersonalEvent> | Partial<AppointmentBlock>
  >({});
  const [onAppointmentSave, setOnAppointmentSave] = useState<any>();
  const [onAppointmentCancel, setOnAppointmentCancel] = useState<any>();
  const [isRescheduling, setIsRescheduling] = useState(false);
  const [reference, setReference] = useState<any>();
  const flags = useFlags();
  const isOpportunity = useIsOpportunity();
  const opportunityFieldEnabled = useOpportunityFieldEnabled();
  const isGlobalPracticeEventEnabled = flags[GLOBAL_PRACTICE_EVENTS_FEATURE];
  const schedulingV2FeatureEnabled = flags[SCHEDULING_V2_FEATURE];
  const eventsRefetchScoping = Boolean(flags[SCHEDULER_EVENTS_REFETCH_SCOPING]);
  const appointmentDraftsEnabled = flags[WEB_SCHEDULER_APPOINTMENTS_FILTER];
  const allowEnteringCancellationReason =
    flags[ALLOW_ENTERING_CANCELLATION_REASON];
  const showAllClinics = flags[SHOW_ALL_CLINICS];
  const appointmentsFilters = useAppSelector(selectFilterValues);
  const appointmentTypesValues = useAppSelector(
    selectAppointmentTypesFilterValues
  );

  const filtersReady = useAppSelector(selectFiltersReady);
  const currentClinic = useAppSelector(selectCurrentClinic);
  const currentUser = useAppSelector(selectCurrentUser);
  const selectedStaff = useAppSelector(selectStaffFilterValues);
  const actionType = useAppSelector(selectSnackbarActionType);

  const setAvailableStaff = useCallback(
    (value: MinimalStaffMember[]): any =>
      dispatch(setSelectedFromAvailableStaff(value as StaffMember[])),
    [dispatch]
  );
  const setShowAvailabilityFromAll = useCallback(
    (value: boolean) => dispatch(setShowFromAllLocations(value)),
    [dispatch]
  );
  const { pathname } = useLocation();

  const { data: allClinics } = useGetClinicsQuery(
    {
      query: "{id,name,timezone}",
      region: hasRegions ? currentRegion?.id : undefined,
      user:
        hasRegions || currentUser?.show_own_clinics_only
          ? currentUser?.id
          : undefined
    },
    {
      skip:
        !currentUser?.id ||
        currentUser?.id === -1 ||
        !currentClinic?.id ||
        (hasRegions && !currentRegion?.id)
    }
  );

  const isDaySheetPage =
    pathname === "/scheduling/day-sheet" || pathname === "/home";
  const isSchedulingPage =
    pathname === "/scheduling" ||
    pathname === "/scheduling/" ||
    pathname === "/scheduling/view" ||
    isDaySheetPage ||
    pathname === "/";

  const selectedClinicIds = useMemo(() => {
    if (currentUser?.show_own_clinics_only) {
      if (userClinicsIds.length > 0) return userClinicsIds;
      if (allClinics?.length) return allClinics.map((c) => c.id);
      return [currentClinic?.id].filter(Boolean);
    }
    return selectedClinics?.filter(Boolean)?.map((clinic) => clinic.id);
  }, [
    currentUser?.show_own_clinics_only,
    userClinicsIds,
    allClinics,
    currentClinic?.id,
    selectedClinics
  ]);

  const appointmentFormId = useMemo(() => {
    return uuidV4();
  }, []);

  const baseFilters = useMemo(() => {
    const baseFilter = isSchedulingPage
      ? getAppointmentsBaseFilter(appointmentsFilters.quickFilterCode, {
          status: VISIBLE_STATUSES_CODES
        })
      : {};

    return isSchedulingPage
      ? {
          ...appointmentsFilters,
          ...(isDaySheetPage && { clinicIds: undefined }),
          baseFilter: {
            ...baseFilter,
            ...(includeSupervisingProvider && {
              include_supervising_provider: true
            })
          },
          eventType: [
            EVENT_TYPE_APPOINTMENT_CODE,
            EVENT_TYPE_SCHEDULE_CODE
          ].join(","),
          query: EVENTS_QUERY
        }
      : {
          eventType: EVENT_TYPE_APPOINTMENT_CODE,
          quickFilterCode: QUICK_FILTER_DEFAULT_TYPE,
          fromDate: format(startOfWeek(new Date()), "yyyy-MM-dd"),
          toDate: format(endOfWeek(new Date()), "yyyy-MM-dd"),
          staffIds: [currentUser.id],
          clinicIds: selectedClinicIds,
          query: EVENTS_QUERY,
          baseFilter: {
            ...(includeSupervisingProvider && {
              include_supervising_provider: true
            })
          }
        };
  }, [
    isSchedulingPage,
    isDaySheetPage,
    appointmentsFilters,
    currentUser.id,
    selectedClinicIds,
    includeSupervisingProvider
  ]);

  const filters = useMemo(() => {
    return {
      ...baseFilters,
      staffIds: baseFilters.staffIds?.length
        ? baseFilters.staffIds
        : [currentUser.id]
    };
  }, [baseFilters, currentUser.id]);

  const { queriesCounters, isLoadingCounters, isInitialLoadingCounters } =
    useAppointmentCounters();

  const { value: dFilters, isDebouncing } = useDebounce(filters, 500);

  // ENG-1252: patient routes (/patients/:id, /patients/new) mount this provider
  // only to expose setupAppointmentModal + appointmentFormId; they never read
  // events/appointments/workShiftEvents from context. Skip the events fetches
  // there so the patient view isn't blocked by a week-window {*} events query.
  const isPatientRoute = pathname.startsWith("/patients/");

  const skipEventsFetching = useMemo(() => {
    return Boolean(
      formOnly ||
        isPatientRoute ||
        !Boolean(currentClinic?.id) ||
        (isSchedulingPage && !filtersReady) ||
        // SN-19768: the first counters request only, so the grid and the counters still
        // appear together. A background counters refetch must never skip this query —
        // `getEventsData` answers a skip with empty arrays, which unmounts every
        // appointment node and leaves the open preview anchored to a detached element.
        isInitialLoadingCounters ||
        !baseFilters.staffIds?.length
    );
  }, [
    formOnly,
    isPatientRoute,
    currentClinic?.id,
    isSchedulingPage,
    filtersReady,
    isInitialLoadingCounters,
    baseFilters.staffIds?.length
  ]);

  const {
    data: events,
    refetch,
    isUninitialized,
    isLoading: isEventsLoading,
    isFetching: isEventsFetching
  } = useSchedulerEvents(
    dFilters,
    {
      skip: skipEventsFetching,
      // Pause polling while the appointment form is open. A mid-edit refetch
      // re-renders the DX scheduler tree, which can fire a spurious
      // onVisibilityChange and close the form — the trigger for SN-17825.
      pollingInterval: editingForm ? 0 : EVENTS_POLL_SECONDS * 1000,
      // Extend cache time for better performance
      refetchOnMountOrArgChange: EVENTS_POLL_SECONDS
    },
    eventsRefetchScoping
  );

  useEffect(() => {
    if (recordMetric) {
      if (isUninitialized || isEventsFetching) {
        if (!reference) {
          setReference(
            datadogRum?.startDurationVital?.("events-fetch", {
              description: "Region/Clinic Switch Events loading",
              context: { currentRegion, currentClinic, currentUser }
            })
          );
        }
      } else {
        if (reference) {
          datadogRum?.stopDurationVital(reference);
          setReference(undefined);
          dispatch(clearRecordMetric());
        }
      }
    }
  }, [
    isEventsFetching,
    recordMetric,
    isUninitialized,
    reference,
    setReference,
    currentRegion,
    currentClinic,
    currentUser,
    dispatch
  ]);

  const workShiftClinicIds: number[] = useMemo(() => {
    if (selectedClinicIds && selectedClinicIds.length > 0) {
      return selectedClinicIds;
    }
    return getClinicIdsFromEvents(
      events,
      allClinics,
      currentClinic?.id,
      isShowByClinicView,
      showAllClinics
    );
  }, [
    selectedClinicIds,
    currentClinic?.id,
    allClinics,
    showAllClinics,
    isShowByClinicView,
    events
  ]);

  const {
    data: workShiftEvents,
    refetch: refetchWorkShiftEvents,
    isUninitialized: isWorkShiftUninitialized,
    isLoading: isWorkShiftLoading,
    isFetching: isWorkShiftFetching
  } = useSchedulerEvents(
    {
      ...dFilters,
      ...(schedulingV2FeatureEnabled ? {} : { clinicIds: workShiftClinicIds }),
      eventType: EVENT_TYPE_WORK_SHIFT_CODE
    },
    {
      skip:
        skipEventsFetching ||
        isEventsFetching ||
        isEventsLoading ||
        isDebouncing,
      // More aggressive caching for work shifts (they change less frequently)
      refetchOnMountOrArgChange: 900 // 15 minutes
    },
    eventsRefetchScoping
  );

  const {
    appointments: rawAppointments,
    personalEvents,
    appointmentBlocks
  } = useMemo(
    () =>
      getEventsData(
        events,
        skipEventsFetching,
        selectedClinics,
        isGlobalPracticeEventEnabled && isShowByClinicView,
        currentClinic,
        selectedStaff,
        includeSupervisingProvider,
        showAllClinics ? (allClinics ?? []) : []
      ),
    [
      events,
      skipEventsFetching,
      selectedClinics,
      isShowByClinicView,
      isGlobalPracticeEventEnabled,
      currentClinic,
      selectedStaff,
      includeSupervisingProvider,
      allClinics,
      showAllClinics
    ]
  );

  // Filter out Web Scheduler DRAFT appointments from "All Appointments" view
  // but keep Google Calendar DRAFT appointments visible.
  // When user explicitly selects "Web Scheduler Appointments" filter, show all drafts.
  const appointments = useMemo(() => {
    if (!appointmentDraftsEnabled || !rawAppointments) {
      return rawAppointments;
    }

    // Don't filter when user explicitly selected "Web Scheduler Appointments" quick filter
    if (
      appointmentsFilters.quickFilterCode === QUICK_FILTER_DRAFT_APPOINTMENTS
    ) {
      return rawAppointments;
    }

    return rawAppointments.filter((apt: Appointment) => {
      // Keep all non-DRAFT appointments
      if (apt.status !== APPOINTMENT_STATUS_DRAFT) {
        return true;
      }

      // Keep DRAFT appointments from Google Calendar
      if (apt.googleCalendar) {
        return true;
      }

      // Filter out Web Scheduler DRAFT appointments
      return false;
    });
  }, [
    rawAppointments,
    appointmentDraftsEnabled,
    appointmentsFilters.quickFilterCode
  ]);

  // Count GC draft appointments shown in the scheduler but excluded from the backend counter
  // (backend excludes all drafts when appointmentDraftsEnabled, but GC drafts remain visible)
  const gcDraftCount = useMemo(() => {
    if (!appointmentDraftsEnabled) return 0;
    return appointments.filter(
      (apt: Appointment) =>
        apt.googleCalendar && apt.status === APPOINTMENT_STATUS_DRAFT
    ).length;
  }, [appointments, appointmentDraftsEnabled]);

  const augmentedCounters = useMemo(
    () => ({
      ...queriesCounters,
      [QUICK_FILTER_DEFAULT_TYPE]:
        queriesCounters[QUICK_FILTER_DEFAULT_TYPE] + gcDraftCount
    }),
    [queriesCounters, gcDraftCount]
  );

  const { workShift, personalWorkShift } = useMemo(
    () =>
      getEventsData(
        workShiftEvents,
        skipEventsFetching,
        selectedClinics,
        isGlobalPracticeEventEnabled && isShowByClinicView,
        currentClinic,
        selectedStaff,
        includeSupervisingProvider,
        showAllClinics ? (allClinics ?? []) : []
      ),
    [
      workShiftEvents,
      skipEventsFetching,
      selectedClinics,
      isShowByClinicView,
      isGlobalPracticeEventEnabled,
      currentClinic,
      selectedStaff,
      includeSupervisingProvider,
      allClinics,
      showAllClinics
    ]
  );

  const isStaffMemberAvailable = useCallback(
    ({
      staffMemberId,
      clinicId,
      startDate,
      endDate
    }: {
      startDate: string;
      endDate?: string;
      staffMemberId?: number;
      clinicId?: number;
    }) => {
      if (!startDate || !endDate || !staffMemberId) return false;

      const result = isStaffMemberAvailableUtil({
        staffMemberId,
        clinicId: !isShowByClinicView
          ? undefined
          : clinicId
            ? clinicId
            : currentClinic?.id,
        startDate: new Date(startDate),
        workShift
      });

      return result;
    },
    [isShowByClinicView, currentClinic?.id, workShift]
  );

  const [addScheduleMutation, { isLoading: isAddScheduleMutationLoading }] =
    useAddScheduleMutation();
  const [
    updateScheduleMutation,
    { isLoading: isUpdateScheduleMutationLoading }
  ] = useUpdateScheduleMutation();
  const [
    deleteScheduleMutation,
    { isLoading: isDeleteScheduleMutationLoading }
  ] = useDeleteScheduleMutation();

  const [addAppointment, { isLoading: isAddAppointmentMutationLoading }] =
    useAddAppointmentMutation();
  const [updateAppointment, { isLoading: isUpdateAppointmentLoading }] =
    useUpdateAppointmentMutation();
  const [deleteAppointment, { isLoading: isDeleteAppointmentLoading }] =
    useDeleteAppointmentMutation();

  const isLoading = useMemo(() => {
    return (
      !skipEventsFetching &&
      [
        isEventsFetching,
        isEventsLoading,
        isLoadingCounters,
        isWorkShiftUninitialized,
        isWorkShiftLoading,
        isWorkShiftFetching,
        isAddScheduleMutationLoading,
        isUpdateScheduleMutationLoading,
        isDeleteScheduleMutationLoading,
        isAddAppointmentMutationLoading,
        isUpdateAppointmentLoading,
        isDeleteAppointmentLoading,
        isDebouncing,
        !allClinics?.length
      ].some(Boolean)
    );
  }, [
    skipEventsFetching,
    isEventsFetching,
    isEventsLoading,
    isLoadingCounters,
    isWorkShiftUninitialized,
    isWorkShiftLoading,
    isWorkShiftFetching,
    isAddScheduleMutationLoading,
    isUpdateScheduleMutationLoading,
    isDeleteScheduleMutationLoading,
    isAddAppointmentMutationLoading,
    isUpdateAppointmentLoading,
    isDeleteAppointmentLoading,
    isDebouncing,
    allClinics?.length
  ]);
  const { errors, resetErrors, handleResponseErrors } = useErrors({
    fieldNames: ["start_date"],
    nonFieldNames: [
      "staff_member_unavailable",
      "room_unavailable",
      DEDICATED_SLOT_RESERVED
    ]
  });
  const confirmationMessages = useMemo(() => {
    const appointment = appointmentMetaRef.current?.data;
    if (appointment) {
      const couldBeDeleted = isAppointmentDeletable(appointment);
      return {
        discardButton: "Discard",
        confirmCancelMessage: "Discard unsaved changes?",
        deleteButton: couldBeDeleted ? "Delete" : "Yes",
        cancelButton: couldBeDeleted ? "Cancel" : "No",
        confirmDeleteMessage: `Are you sure you want to ${
          couldBeDeleted ? "delete" : "cancel"
        } this ${
          isPersonalEvent(appointment)
            ? "personal event"
            : isClinicEvent(appointment)
              ? "clinic event"
              : isAppointmentBlock(appointment)
                ? "dedicated appointment slot"
                : isPersonalWorkShiftEvent(appointment)
                  ? "work shift"
                  : "appointment"
        }?`
      };
    } else {
      return {
        discardButton: "Discard",
        deleteButton: "Delete",
        cancelButton: "Cancel",
        confirmDeleteMessage:
          "Are you sure you want to delete this appointment?",
        confirmCancelMessage: "Discard unsaved changes?"
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointmentMetaRef.current?.data]);

  const clearErrorNotifications = useCallback(() => {
    resetErrors();
    dispatch(setOpen(false));
    dispatch(setCustomMessage(undefined));
  }, [resetErrors, dispatch]);

  const handleAppointmentDeletion = useCallback(
    (appointment: AppointmentModel | PersonalEvent | AppointmentBlock) => {
      return (eventDeletionInput: DeleteAppointmentInput) => {
        if (isAppointmentDeletable(appointment)) {
          return deleteAppointment(eventDeletionInput);
        } else {
          const appointmentId =
            (appointment as Appointment & { modelId?: number }).modelId ??
            (appointment as Appointment).id;
          return updateAppointment({
            id: Number(appointmentId),
            status: APPOINTMENT_STATUS_CANCELED,
            send_cancellation_message:
              eventDeletionInput.send_cancellation_message
          });
        }
      };
    },
    [deleteAppointment, updateAppointment]
  );

  // wrapped in useCallback to avoid re-rendering the component when the function is called
  const removeAppointment = useCallback(
    (
      eventId: number,
      updateType: string = "",
      sendCancellationMessage?: boolean
    ) => {
      const { data } = appointmentMetaRef.current || {};

      if (!data) {
        return;
      }

      let appointment: AppointmentModel | PersonalEvent | AppointmentBlock =
        data;

      const isCouldBeDeleted = isAppointmentDeletable(appointment);
      dispatch(
        setLabel(
          isPersonalEvent(appointment)
            ? "Personal Event"
            : isClinicEvent(appointment)
              ? "Clinic Event"
              : isAppointmentBlock(appointment)
                ? "Dedicated Appointment Slot"
                : isPersonalWorkShiftEvent(appointment)
                  ? "Work Shift"
                  : "Appointment"
        )
      );
      dispatch(setActionType(isCouldBeDeleted ? "delete" : "cancel"));
      setAppointmentIdMutationInProgress(Number(appointment?.id));

      const deleteAction = isAppointment(appointment)
        ? handleAppointmentDeletion(appointment)
        : deleteScheduleMutation;

      let deleteInput: any;
      if (!isAppointment(appointment)) {
        deleteInput = {
          scheduleId: Number.isInteger(eventId) ? eventId : appointment.modelId
        } as DeleteScheduleInput;
        if (
          updateType &&
          (appointment.recurrencePeriod ||
            appointment.rrule ||
            appointment.schedule?.rrule)
        ) {
          if ([CURRENT_EVENT, CURRENT_AND_FOLLOWING].includes(updateType)) {
            deleteInput["instance_start_date"] = format(
              new Date(appointment.startDate),
              "yyyy-MM-dd"
            );
            if (updateType === CURRENT_EVENT) {
              deleteInput["instance_end_date"] = format(
                new Date(appointment.endDate || appointment.startDate),
                "yyyy-MM-dd"
              );
            }
          }
        }
      } else {
        deleteInput = {
          appointmentId: eventId,
          ...(sendCancellationMessage === undefined
            ? {}
            : { send_cancellation_message: sendCancellationMessage })
        } as DeleteAppointmentInput;

        if (updateType && appointment.schedule) {
          if ([ALL_EVENTS, CURRENT_AND_FOLLOWING].includes(updateType)) {
            deleteInput["start_recurrence_id"] =
              updateType === CURRENT_AND_FOLLOWING
                ? appointment.id
                : appointment.schedule.appointment || undefined;
          }
        }
      }

      deleteAction(deleteInput as any).then(() => {
        if (!isUninitialized && !isEventsLoading) {
          safeRefetch(refetch, isUninitialized);
        }
        if (!isWorkShiftUninitialized && !isWorkShiftLoading) {
          safeRefetch(refetchWorkShiftEvents, isWorkShiftUninitialized);
        }
        dispatch(hidePreview());
        dispatch(setLabel("Appointment"));
        dispatch(setCustomMessage(undefined));
        dispatch(setOpen(1000));
        setAppointmentIdMutationInProgress(undefined);
      });
    },
    [
      deleteScheduleMutation,
      dispatch,
      handleAppointmentDeletion,
      isEventsLoading,
      isUninitialized,
      isWorkShiftLoading,
      isWorkShiftUninitialized,
      refetch,
      refetchWorkShiftEvents
    ]
  );

  // wrapped in useCallback to avoid re-rendering the component when the function is called
  const updateAppointmentStatus = useCallback(
    (
      initAppointmentOutcomeDialog: any,
      checkUndeliveredDevices?: (appointment: Appointment) => void,
      checkScheduleNextAppointment?: (appointment: Appointment) => void
    ) => {
      /**
       * `sendCancellationMessage` is staff's answer to "Send cancellation message?" when the
       * caller already asked it (the preview's cancel confirmation). A cancel that reaches here
       * unanswered on a practice set to "Ask each time" asks before writing anything — in the
       * outcome dialog when one opens, in the confirm dialog otherwise.
       */
      const apply = (
        appointment: Appointment,
        status: number,
        sendCancellationMessage?: boolean
      ): Promise<unknown> | void => {
        dispatch(
          setLabel(
            isPersonalEvent(appointment)
              ? "Personal Event"
              : isClinicEvent(appointment)
                ? "Clinic Event"
                : isAppointmentBlock(appointment)
                  ? "Dedicated Appointment Slot"
                  : isPersonalWorkShiftEvent(appointment)
                    ? "Work Shift"
                    : "Appointment"
          )
        );
        dispatch(
          setActionType(
            status === APPOINTMENT_STATUS_CANCELED ? "cancel" : "update"
          )
        );
        setAppointmentIdMutationInProgress(appointment.id);

        const collectOutcomeIsEnabled = shouldCollectOutcome({
          status,
          practiceCollectsOutcome: currentPractice?.collect_outcome,
          isOpportunityAppointment: isOpportunity(
            appointment,
            appointment.appointmentType
          )
        });

        const collectCancellationReasonIsEnabled =
          [APPOINTMENT_STATUS_CANCELED, APPOINTMENT_STATUS_NO_SHOW].includes(
            status
          ) && allowEnteringCancellationReason;

        if (
          status === APPOINTMENT_STATUS_CANCELED &&
          sendCancellationMessage === undefined &&
          !collectOutcomeIsEnabled &&
          !collectCancellationReasonIsEnabled &&
          Boolean(appointment.patient) &&
          asksCancellationMessage(currentPractice)
        ) {
          const { content, answer } = withCancellationMessageChoice(dispatch, {
            who: [
              appointment.patient?.first_name,
              appointment.patient?.last_name
            ]
              .filter(Boolean)
              .join(" "),
            options: { actionButtonText: "Cancel appointment" }
          });
          dispatch(setTitle("Cancel this appointment?"));
          dispatch(setContent(content));
          dispatch(
            setOnChange(() => {
              const send = answer();
              if (send === undefined) return;
              void apply(appointment, status, send);
            })
          );
          dispatch(
            setOnCancel(() => setAppointmentIdMutationInProgress(undefined))
          );
          dispatch(setConfirmChangeDialogOpen(true));
          return;
        }

        if (collectOutcomeIsEnabled || collectCancellationReasonIsEnabled) {
          const appointmentId =
            (appointment as Appointment & { modelId?: number }).modelId ??
            appointment.id;
          initAppointmentOutcomeDialog(
            Number(appointmentId),
            () => {
              if (!isUninitialized && !isEventsLoading) {
                safeRefetch(refetch, isUninitialized);
              }
              if (!isWorkShiftUninitialized && !isWorkShiftLoading) {
                safeRefetch(refetchWorkShiftEvents, isWorkShiftUninitialized);
              }
              dispatch(hidePreview());
              dispatch(setLabel("Appointment"));
              dispatch(setCustomMessage(undefined));
              dispatch(setOpen(1000));
              setAppointmentIdMutationInProgress(undefined);

              if (status === APPOINTMENT_STATUS_COMPLETED) {
                if (checkUndeliveredDevices) {
                  checkUndeliveredDevices(appointment);
                }
                if (checkScheduleNextAppointment) {
                  checkScheduleNextAppointment(appointment);
                }
              }
            },
            () => {
              setAppointmentIdMutationInProgress(undefined);
            },
            status,
            undefined,
            undefined,
            { sendCancellationMessage }
          );
        } else {
          const appointmentId =
            (appointment as Appointment & { modelId?: number }).modelId ??
            appointment.id;
          return updateAppointment({
            id: Number(appointmentId),
            status,
            send_cancellation_message: sendCancellationMessage
          }).then(() => {
            if (!isUninitialized && !isEventsLoading && !isEventsFetching) {
              safeRefetch(refetch, isUninitialized);
            }
            if (!isWorkShiftUninitialized && !isWorkShiftLoading) {
              safeRefetch(refetchWorkShiftEvents, isWorkShiftUninitialized);
            }
            dispatch(hidePreview());
            dispatch(setLabel("Appointment"));

            dispatch(setCustomMessage(undefined));
            dispatch(setOpen(1000));

            setAppointmentIdMutationInProgress(undefined);

            if (status === APPOINTMENT_STATUS_COMPLETED) {
              if (checkUndeliveredDevices) {
                checkUndeliveredDevices(appointment);
              }
              if (checkScheduleNextAppointment) {
                checkScheduleNextAppointment(appointment);
              }
            }
          });
        }
      };
      return apply;
    },
    [
      currentPractice,
      dispatch,
      isEventsFetching,
      isEventsLoading,
      isUninitialized,
      isWorkShiftLoading,
      isWorkShiftUninitialized,
      refetch,
      refetchWorkShiftEvents,
      updateAppointment,
      allowEnteringCancellationReason,
      isOpportunity
    ]
  );

  // wrapped in useCallback to avoid re-rendering the component when the function is called
  const setupAppointmentModal = useCallback(
    (
      appointment: Appointment | PersonalEvent | AppointmentBlock,
      actionType: ActionType,
      onAppointmentSave: () => void,
      onAppointmentCancel: () => void,
      isRescheduling = false
    ) => {
      clearErrorNotifications();
      dispatch(setActionType(actionType));

      if (actionType === "add") {
        // Reschedule seeds spread the source appointment — recurrence must not
        // carry over, or saving the new appointment would silently spawn a
        // whole new recurring series (SN-18585).
        appointment = stripRecurrenceCarryover(appointment);
      }

      setOnAppointmentCancel(() => onAppointmentCancel);
      setOnAppointmentSave(() => onAppointmentSave);
      setIsRescheduling(isRescheduling);
      setOriginalAppointment(appointment);

      setAvailableStaff(
        appointment?.staffMember ? [appointment.staffMember] : []
      );

      const {
        patient,
        preferredClinic,
        preferredProvider,
        patientReferralSource,
        patientSubReferralSource
      } = getAppointmentPatientData(appointment);

      const clinicToOverride =
        allClinics?.find((clinic) => clinic.id === appointment?.clinicId) ??
        appointment.clinic;

      const clinic =
        appointment?.clinic ||
        preferredClinic ||
        (multiClinic ? null : currentClinic);
      setShowAvailabilityFromAll(!clinic);

      const formValues = {
        id: appointment?.id,
        event_type: appointment?.type,
        title: appointment?.title ?? "",
        notes: appointment?.notes,
        staff_member: appointment?.staffMember || preferredProvider,
        appointment_date: appointment?.startDate
          ? new Date(appointment.startDate)
          : new Date(),
        start_time: appointment?.startDate
          ? new Date(appointment.startDate)
          : new Date(),
        end_time: appointment?.endDate
          ? new Date(appointment.endDate)
          : addMinutes(new Date(), 60),
        clinic,
        timezone: clinic?.timezone,
        clinicIdToOverride: appointment?.clinicId,
        patient
      };

      if (clinicToOverride && clinicToOverride?.id !== appointment.clinic?.id) {
        formValues.clinic = clinicToOverride ?? formValues.clinic;
      }

      if (
        isPersonalEvent(appointment) ||
        isClinicEvent(appointment) ||
        isPersonalWorkShiftEvent(appointment)
      ) {
        setCurrentAppointment({
          ...formValues,
          modelId: appointment?.modelId || appointment?.id,
          is_global_practice_event: appointment?.isGlobalPracticeEvent,
          recurrence_period: appointment?.recurrencePeriod,
          recurrence_day: appointment?.recurrenceDay,
          recurrence_week: appointment?.recurrenceWeek,
          appointment_type: appointment?.appointment_type,
          is_recurring: Boolean(appointment?.recurrencePeriod),
          event_end_date: appointment?.endRecurrenceDate
            ? parseBackendDateOnly(appointment.endRecurrenceDate)
            : null,
          staff_members: appointment.staff_members ?? [],
          rrule: appointment.rrule
        } as Partial<AppointmentFormValues>);
      } else if (isAppointmentBlock(appointment)) {
        setCurrentAppointment({
          ...formValues,
          modelId: appointment?.modelId || appointment?.id,
          recurrence_period: appointment?.recurrencePeriod,
          recurrence_day: appointment?.recurrenceDay,
          recurrence_week: appointment?.recurrenceWeek,
          is_recurring: Boolean(appointment?.recurrencePeriod),
          event_end_date: appointment?.endRecurrenceDate
            ? parseBackendDateOnly(appointment.endRecurrenceDate)
            : null,
          appointment_type: appointment?.appointmentType,
          rrule: appointment.rrule
        } as Partial<AppointmentFormValues>);
      } else {
        // Calculate if the appointment currently overlaps with any blocks
        const appointmentStartDate = appointment?.startDate
          ? new Date(appointment.startDate).getTime()
          : undefined;
        const appointmentEndDate = appointment?.endDate
          ? new Date(appointment.endDate).getTime()
          : undefined;
        const staffMemberId = appointment?.staffMemberId;

        const currentIntersectingBlocks =
          appointmentStartDate && appointmentEndDate && appointmentBlocks
            ? appointmentBlocks.filter(
                (block: any) =>
                  block.staffMemberId === staffMemberId &&
                  ((isWithinInterval(new Date(appointmentStartDate), {
                    start: new Date(block.startDate),
                    end: new Date(block.endDate)
                  }) &&
                    !isEqual(
                      new Date(appointmentStartDate),
                      new Date(block.endDate)
                    )) ||
                    (isWithinInterval(new Date(appointmentEndDate), {
                      start: new Date(block.startDate),
                      end: new Date(block.endDate)
                    }) &&
                      !isEqual(
                        new Date(appointmentEndDate),
                        new Date(block.startDate)
                      )))
              )
            : [];

        // ENG-2105: a multi-type slot accepts any of its types, not only the first.
        const initiallyOverlapsWithBlock = currentIntersectingBlocks.some(
          (block: any) =>
            !slotAcceptsType(
              block.appointmentTypes ??
                (block.appointmentType ? [block.appointmentType] : []),
              appointment?.appointmentType?.id
            )
        );

        setCurrentAppointment({
          ...formValues,
          // The card in a supervising provider's column carries a synthetic id
          // (`get-events-data.util.ts`) and the real one on `modelId`; saving with the
          // synthetic id PATCHes a URL that does not exist (DEV-3288). Update only: a
          // reschedule seed drops `id` but keeps `modelId`, and must still create.
          id:
            actionType === "update"
              ? ((appointment as Appointment & { modelId?: number }).modelId ??
                formValues.id)
              : formValues.id,
          updated_at: (appointment as Appointment)?.updated_at,
          status: appointment?.status,
          room: appointment?.room,
          appointment_type: appointment?.appointmentType,
          referral_source:
            appointment?.referralSource &&
            typeof appointment.referralSource === "object"
              ? String(appointment.referralSource.id)
              : String(
                  appointment?.referralSource || patientReferralSource || ""
                ),
          sub_referral_source:
            appointment?.subReferralSource &&
            typeof appointment.subReferralSource === "object"
              ? appointment.subReferralSource.id
              : appointment?.subReferralSource ||
                patientSubReferralSource ||
                "",
          sub_referral_source_object:
            appointment?.subReferralSourceObject || null,
          referring_physician:
            appointment?.referringPhysician &&
            typeof appointment.referringPhysician === "object"
              ? appointment.referringPhysician
              : appointment?.referringPhysician || "",
          referral: appointment?.referral,
          companion_type: appointment?.companionType || "",
          companion_name: appointment?.companionName,
          patient: appointment?.patient,
          should_notify: appointment.shouldNotify,
          should_create_schedule_for_staff:
            appointment.shouldCreateScheduleForStaff,
          web_patient: appointment?.webPatient,
          notification_method: appointment?.notificationMethod,
          notify_both_channels: appointment?.notifyBothChannels,
          intake_form_request: appointment?.intake_form_request,
          should_create_intake_form_request:
            appointment?.should_create_intake_form_request,
          rescheduled_appointment_id: appointment?.rescheduled_appointment_id,
          schedule: appointment?.schedule,
          assigned_tags: appointment?.assignedTags,
          supervising_provider: appointment?.supervising_provider ?? null,
          is_direct_supervisor: appointment?.is_direct_supervisor ?? false,
          // Only the MANUAL half is seeded. The backend copies an opportunity TYPE's flag
          // onto the row at create, so seeding the raw row value would carry that
          // type-derived `true` into form state; switching to a plain type would then leave
          // the box ticked and the save would send `true`, marking the visit an Opportunity
          // for good. Reading the manual half instead keeps the box ticked under an
          // opportunity type (it reads the type) and unticked once the type changes.
          is_opportunity: isManuallyFlagged(
            appointment,
            appointment?.appointmentType,
            opportunityFieldEnabled
          ),
          overwrite_block:
            actionType === "update" && initiallyOverlapsWithBlock
              ? true
              : undefined,
          room_start_moment: (appointment as Appointment)
            .room_start_moment as unknown as Date | null,
          room_end_moment: (appointment as Appointment)
            .room_end_moment as unknown as Date | null
        } as Partial<AppointmentFormValues>);
      }
      dispatch(setOpen(false));
    },
    [
      allClinics,
      appointmentBlocks,
      clearErrorNotifications,
      currentClinic,
      dispatch,
      multiClinic,
      opportunityFieldEnabled,
      setAvailableStaff,
      setShowAvailabilityFromAll
    ]
  );

  useEffect(() => {
    if (editingForm) {
      dispatch(openForm(appointmentFormId));
    } else {
      dispatch(closeForm());
    }
  }, [editingForm, dispatch, appointmentFormId]);

  const handleAppointmentActionResult = useCallback(
    (returnedAppointment: any, options?: { isMove?: boolean }) => {
      setAppointmentIdMutationInProgress(undefined);
      const handledErrors = handleResponseErrors(returnedAppointment);
      // ENG-2105: a drag saves without the form, so its dedicated-slot refusal says so.
      const responseErrors =
        handledErrors && options?.isMove
          ? {
              ...handledErrors,
              nonFieldErrors: withNotMovedPrefix(handledErrors.nonFieldErrors)
            }
          : handledErrors;

      // ENG-1831: when the practice's cross-clinic conflict gate is on, the staff-busy
      // error is presented by the ProviderConflictModal — the global snackbar echoing
      // the raw message on top of it restores the bare-error UX the modal replaces.
      const snackbarErrors =
        responseErrors && isCrossClinicConflictGateOn
          ? {
              ...responseErrors,
              nonFieldErrors: responseErrors.nonFieldErrors.filter(
                (error) => !isStaffBusyError(error)
              )
            }
          : responseErrors;
      const showSnackbar =
        snackbarErrors &&
        (Object.keys(snackbarErrors.fieldErrors).length > 0 ||
          snackbarErrors.nonFieldErrors.length > 0);

      let isSuccess = false;
      if (responseErrors) {
        if (showSnackbar) {
          dispatch(
            setCustomMessage(
              <ErrorStateSnackbarContent
                errors={snackbarErrors}
                showAsWarning
              />
            )
          );
        }
      } else if ("data" in returnedAppointment) {
        isSuccess = true;
        dispatch(closeForm());
        if (!isUninitialized && !isEventsLoading) {
          safeRefetch(refetch, isUninitialized);
        }
        if (!isWorkShiftUninitialized && !isWorkShiftLoading) {
          safeRefetch(refetchWorkShiftEvents, isWorkShiftUninitialized);
        }
        if (onAppointmentSave) {
          onAppointmentSave();
        }
      } else {
        dispatch(
          setCustomMessage(
            getFriendlyAppointmentSaveError(returnedAppointment) ??
              "An Error has Occurred, please reach out to support."
          )
        );
      }
      dispatch(setLabel("Appointment"));
      if (isSuccess) {
        dispatch(setCustomMessage(undefined));
      }
      // Suppressed snackbar (modal-owned error): don't open an empty snackbar either.
      if (isSuccess || showSnackbar || !responseErrors) {
        dispatch(setOpen(isSuccess ? 1000 : SNACKBAR_DURATION_FOREVER));
      }
      return { isSuccess, result: returnedAppointment, responseErrors };
    },
    [
      handleResponseErrors,
      onAppointmentSave,
      refetch,
      refetchWorkShiftEvents,
      dispatch,
      isUninitialized,
      isEventsLoading,
      isWorkShiftUninitialized,
      isWorkShiftLoading,
      isCrossClinicConflictGateOn
    ]
  );

  const handleRecurringEventUpdate = useCallback(
    (
      appointmentAction: any,
      eventInput:
        | (Partial<Schedule> & ScheduleInstanceInput)
        | (Partial<Appointment> & AppointmentRecurrenceInput),
      actionLabel: string
    ) => {
      const message = `Are you sure you want to update this ${actionLabel}?`;
      dispatch(setTitle(message));

      const eventId = Number(eventInput?.id);
      setAppointmentIdMutationInProgress(eventId);

      dispatch(
        setOptions({
          actionButtonText: "Update"
        })
      );

      dispatch(
        setContent(
          <EditRecurringEventDialogContent
            eventType={actionLabel}
            eventEndTime={
              appointmentMetaRef.current?.data?.endDate
                ? String(appointmentMetaRef.current.data.endDate)
                : undefined
            }
            changeCallback={(updateType) => {
              const { data: appointment } = appointmentMetaRef.current || {};
              const instanceStartDate = format(
                new Date(appointment?.startDate ?? ""),
                "yyyy-MM-dd"
              );
              const instanceEndDate = format(
                new Date(appointment?.endDate ?? ""),
                "yyyy-MM-dd"
              );
              if (isAppointment(appointment) && appointment.schedule) {
                if ([ALL_EVENTS, CURRENT_AND_FOLLOWING].includes(updateType)) {
                  (
                    eventInput as Partial<Appointment> &
                      AppointmentRecurrenceInput
                  )["start_recurrence_id"] =
                    updateType === CURRENT_AND_FOLLOWING
                      ? appointment.id
                      : appointment.schedule.appointment || undefined;
                }
              } else {
                if (
                  [CURRENT_EVENT, CURRENT_AND_FOLLOWING].includes(updateType)
                ) {
                  (eventInput as Partial<Schedule> & ScheduleInstanceInput)[
                    "instance_start_date"
                  ] = instanceStartDate;
                  if (updateType === CURRENT_EVENT) {
                    (eventInput as Partial<Schedule> & ScheduleInstanceInput)[
                      "instance_end_date"
                    ] = instanceEndDate;
                  }
                } else {
                  if (instanceStartDate === (eventInput as any)["start_date"]) {
                    delete (eventInput as any)["start_date"];
                  }
                }
              }

              setOptions({
                actionButtonText: ""
              });
              //Send update recurring event request
              appointmentAction(eventInput).then(handleAppointmentActionResult);
            }}
          />
        )
      );

      dispatch(
        setOnCancel(() => {
          setAppointmentIdMutationInProgress(undefined);
          setOptions({
            actionButtonText: ""
          });
          dispatch(setEditRecurringEventType(""));
        })
      );
      dispatch(setConfirmChangeDialogOpen(true));
    },
    [dispatch, handleAppointmentActionResult]
  );

  const handleCreateAppointment = useCallback(
    (
      appointmentData: Partial<Appointment> | Partial<Schedule>,
      overrideActionType?: string
    ) => {
      clearErrorNotifications();
      let appointmentAction: any;
      let addAction: any = addAppointment;
      let updateAction: any = updateAppointment;
      let actionLabel = "Appointment";
      const eventInput = appointmentData;

      if (isPersonalEvent(eventInput)) {
        actionLabel = "Personal Event";
        addAction = addScheduleMutation;
        updateAction = updateScheduleMutation;
      } else if (isClinicEvent(eventInput)) {
        actionLabel = "Clinic Event";
        addAction = addScheduleMutation;
        updateAction = updateScheduleMutation;
      } else if (isPersonalWorkShiftEvent(eventInput)) {
        actionLabel = "Work Shift";
        addAction = addScheduleMutation;
        updateAction = updateScheduleMutation;
      } else if (isAppointmentBlock(eventInput)) {
        actionLabel = "Dedicated Appointment Slot";
        addAction = addScheduleMutation;
        updateAction = updateScheduleMutation;
      } else if (isAppointment(eventInput)) {
        if (eventInput.companionType) {
          eventInput.companion_present = true;
        }
        if (
          isAppointment(originalAppointment) &&
          originalAppointment.status === APPOINTMENT_STATUS_DRAFT &&
          Boolean(eventInput.patient)
        ) {
          eventInput.status = resolveDraftConfirmationStatus(
            eventInput.startDate ?? originalAppointment.startDate
          );
        }
      }
      dispatch(setLabel(actionLabel));
      if ((overrideActionType ?? actionType) === "add") {
        appointmentAction = addAction;
        if (overrideActionType) {
          dispatch(setActionType(overrideActionType as ActionType));
        }
      } else {
        appointmentAction = updateAction;
      }

      const isRRule = "rrule" in eventInput && eventInput.rrule;
      const isRecurrencePeriod =
        ("recurrence_period" in eventInput && eventInput.recurrence_period) ||
        currentAppointment.recurrence_period ||
        currentAppointment.schedule;

      if (
        (isRRule || isRecurrencePeriod) &&
        appointmentAction === updateAction
      ) {
        dispatch(closeForm());
        handleRecurringEventUpdate(
          appointmentAction,
          eventInput as Partial<Schedule>,
          actionLabel
        );
        return new Promise((resolve) => resolve({}));
      } else {
        return appointmentAction(eventInput).then(
          handleAppointmentActionResult
        );
      }
    },
    [
      clearErrorNotifications,
      addAppointment,
      updateAppointment,
      dispatch,
      actionType,
      currentAppointment.recurrence_period,
      addScheduleMutation,
      updateScheduleMutation,
      originalAppointment,
      handleRecurringEventUpdate,
      handleAppointmentActionResult,
      currentAppointment.schedule
    ]
  );

  const handleMovedEvent = useCallback(
    (changedData: MovedEventInput) => {
      clearErrorNotifications();

      const {
        id,
        type,
        startDate,
        endDate,
        staffMemberId,
        clinicId,
        timezone,
        modelId,
        prevStartDate,
        room_start_moment,
        room_end_moment,
        allowOverlap
      } = changedData;

      // Every schedule row goes to /schedules/ (ENG-2297). Clinic events and work shifts
      // used to fall through to the appointment update below with the SCHEDULE's id,
      // which 404'd — or moved a patient's appointment that happened to share the number.
      if (isScheduleKind(type)) {
        const scheduleEvent = scheduleMoveBody({
          type,
          id: modelId || id,
          startDate,
          endDate,
          staffMemberId,
          clinicId,
          prevStaffMemberId: changedData.prevStaffMemberId,
          staffMemberIds: changedData.staffMemberIds,
          isGlobalPracticeEvent: changedData.isGlobalPracticeEvent
        });

        return updateScheduleMutation(scheduleEvent).then(
          handleAppointmentActionResult
        );
      } else {
        const clinic = allClinics?.find((clinic) => clinic.id === clinicId);
        const appointment: Partial<Appointment> = {
          id: modelId || id,
          staffMemberId,
          clinic,
          startDate: format(
            convertDateFromCurrentTimezoneToTimezone(startDate, timezone),
            "yyyy-MM-dd HH:mm:ssXXX"
          ),
          endDate: format(
            convertDateFromCurrentTimezoneToTimezone(endDate, timezone),
            "yyyy-MM-dd HH:mm:ssXXX"
          ),
          // ENG-1848. Gate off: the key is absent, so the payload is byte-identical to
          // before this feature. Gate on: send the flag explicitly on every move —
          // otherwise `Appointment.clean()` keys off the value STORED at create time,
          // so an appointment once booked through "Book anyway" carries a permanent
          // bypass and every later drag of it silently double-books.
          //
          // The default is the practice's legacy "always allow overlap" setting, NOT a
          // bare false: that setting is the master no-op for this gate everywhere else
          // (the cell-click condition and the create serializer's own default both
          // defer to it), and a drag that enforced while the form did not would be
          // exactly the skew ENG-1831's single derived boolean exists to prevent.
          ...(isCrossClinicConflictGateOn && {
            allowOverlap:
              allowOverlap ?? Boolean(currentPractice?.scheduling_allow_overlap)
          })
        };

        if (prevStartDate && room_start_moment && room_end_moment) {
          const oldStart = new Date(prevStartDate);
          const roomStartOffset = differenceInMinutes(
            new Date(room_start_moment),
            oldStart
          );
          const roomEndOffset = differenceInMinutes(
            new Date(room_end_moment),
            oldStart
          );
          appointment.room_start_moment = format(
            convertDateFromCurrentTimezoneToTimezone(
              addMinutes(startDate, roomStartOffset),
              timezone
            ),
            "yyyy-MM-dd HH:mm:ssXXX"
          );
          appointment.room_end_moment = format(
            convertDateFromCurrentTimezoneToTimezone(
              addMinutes(startDate, roomEndOffset),
              timezone
            ),
            "yyyy-MM-dd HH:mm:ssXXX"
          );
        }

        return updateAppointment(appointment)
          .then((result) =>
            handleAppointmentActionResult(result, { isMove: true })
          )
          .then((outcome) => {
            if (
              isCrossClinicConflictGateOn &&
              hasStaffBusyError(outcome.responseErrors)
            ) {
              dragReplayFiredRef.current = false;
              setDragConflict(changedData);
            }
            return outcome;
          });
      }
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      handleAppointmentActionResult,
      updateAppointment,
      isCrossClinicConflictGateOn,
      currentPractice?.scheduling_allow_overlap
    ]
  );

  const handleCloneAppointment = useCallback(
    (appointment: Appointment | PersonalEvent, droppedMeta: any) => {
      const isPersonal = isPersonalEvent(appointment);
      const isClinic = isClinicEvent(appointment);
      const isWorkShift = isPersonalWorkShiftEvent(appointment);
      const isBlock = isAppointmentBlock(appointment);
      const eventTypeText = isPersonal
        ? "personal event"
        : isBlock
          ? "dedicated appointment slot"
          : "appointment";

      const newStaffMember = selectedStaff.find(
        (staff) => staff.id === droppedMeta.staffMemberId
      );

      const isDifferentStaffMember =
        appointment.staffMemberId !== newStaffMember?.id;

      const isDifferentClinic = !isSameClinicDrop(
        appointment.clinicId,
        droppedMeta?.clinicId
      );

      const newClinic = allClinics?.find(
        (clinic) => clinic.id === droppedMeta.clinicId
      );

      const confirmationText = `Are you sure you want to clone this ${eventTypeText}?`;

      dispatch(setTitle(confirmationText));
      dispatch(
        setContent(
          <CloneEventDialogContent
            confirmationText={confirmationText}
            startDate={droppedMeta.startDate}
            isDifferentStaffMember={isDifferentStaffMember}
            isDifferentClinic={isDifferentClinic}
            newStaffMemberFullName={newStaffMember?.fullName ?? ""}
            newClinicName={newClinic?.name ?? ""}
          />
        )
      );

      dispatch(
        setOnChange(() => {
          setAppointmentIdMutationInProgress(Number(appointment?.id));

          const duration = differenceInMinutes(
            new Date(String(appointment?.endDate)),
            new Date(String(appointment?.startDate))
          );
          const endDate = addMinutes(droppedMeta.startDate, duration);
          let eventDataToClone: Partial<Appointment> | Partial<Schedule> = {};

          if (isPersonal || isClinic || isWorkShift || isBlock) {
            eventDataToClone = {
              type: appointment.type,
              is_available: isWorkShift || isBlock,
              title: appointment.title,
              start_time: format(droppedMeta.startDate, "HH:mm:ss"),
              start_date: format(droppedMeta.startDate, "yyyy-MM-dd"),
              end_time: format(droppedMeta.endDate, "HH:mm:ss"),
              end_date: format(droppedMeta.endDate, "yyyy-MM-dd"),
              clinic: newClinic?.id ?? appointment.clinic.id,
              staff_member:
                isPersonal || isWorkShift || isBlock
                  ? droppedMeta.staffMemberId
                  : null,
              ...(isBlock && (appointment as any).appointmentType
                ? {
                    appointment_types: [(appointment as any).appointmentType.id]
                  }
                : {})
            };
          } else {
            const clinic = newClinic ?? appointment.clinic;
            eventDataToClone = {
              ...omit(appointment, [
                "id",
                "status",
                "room_start_moment",
                "room_end_moment"
              ]),
              clinic,
              staffMemberId: newStaffMember?.id ?? appointment.staffMember.id,
              startDate: format(
                convertDateFromCurrentTimezoneToTimezone(
                  droppedMeta.startDate,
                  clinic?.timezone
                ),
                "yyyy-MM-dd HH:mm:ssXXX"
              ),
              endDate: format(
                convertDateFromCurrentTimezoneToTimezone(
                  endDate,
                  clinic?.timezone
                ),
                "yyyy-MM-dd HH:mm:ssXXX"
              )
            };
          }

          handleCreateAppointment(eventDataToClone, "add");
        })
      );
      dispatch(
        setOnCancel(() => {
          setAppointmentIdMutationInProgress(undefined);
        })
      );
      startTransition(() => {
        dispatch(setConfirmChangeDialogOpen(true));
      });
    },
    [selectedStaff, allClinics, dispatch, handleCreateAppointment]
  );

  const handleMoveEvent = useCallback(
    async (event: Appointment | PersonalEvent, droppedMeta: any) => {
      const newStaffMember = selectedStaff.find(
        (staff) => staff.id === droppedMeta.staffMemberId
      );

      const isDifferentStaffMember = event.staffMemberId !== newStaffMember?.id;

      const isDifferentClinic = !isSameClinicDrop(
        event.clinicId,
        droppedMeta?.clinicId
      );

      const prevClinicName =
        allClinics?.find((clinic) => clinic.id === event.clinicId)?.name ?? "";

      const newClinicName =
        allClinics?.find((clinic) => clinic.id === droppedMeta.clinicId)
          ?.name ?? "";

      const previousStartDate = format(
        new Date(String(event.startDate)),
        "EEE MMM dd, yyyy hh:mm aaa"
      );

      const previousEndDate = format(
        new Date(String(event.endDate)),
        "hh:mm aaa"
      );

      const newStartDate = format(
        droppedMeta.startDate,
        "EEE MMM dd, yyyy hh:mm aaa"
      );

      const newEndDate = format(droppedMeta.endDate, "hh:mm aaa");

      const isSameDate = previousStartDate === newStartDate;

      let hasRoomConflict = false;
      let roomName = undefined;
      if (isAppointment(event) && event.room?.id) {
        roomName = event.room?.name;
        const isRoomAvailableResult = await isRoomAvailable(
          event.room,
          new Date(droppedMeta.startDate),
          new Date(droppedMeta.startDate),
          new Date(droppedMeta.endDate),
          event.title,
          event.patient,
          event.clinic,
          event.id
        );
        hasRoomConflict = !isRoomAvailableResult;
      }
      // ENG-2105: while enforced, say before Yes when the drop lands in a slot reserved for other
      // types. An override user's move goes through; front desk's is refused after Yes.
      const reservedSlotWarning = isAppointment(event)
        ? dragReservedSlotWarning({
            isEnforced: isDedicatedSlotEnforced,
            blocks: appointmentBlocks ?? [],
            target: {
              staffMemberId: droppedMeta.staffMemberId ?? event.staffMemberId,
              clinicId: droppedMeta.clinicId ?? event.clinicId,
              start: new Date(droppedMeta.startDate),
              end: new Date(droppedMeta.endDate),
              appointmentTypeId: event.appointmentType?.id
            }
          })
        : null;
      dispatch(
        // The event's own kind (ENG-2313): a clinic event is not an appointment.
        setTitle(moveEventTitle(event.type))
      );
      dispatch(
        setContent(
          <MoveEventDialogContent
            isSameDate={isSameDate}
            isDifferentClinic={isDifferentClinic}
            isDifferentStaffMember={isDifferentStaffMember}
            prevStartDate={previousStartDate}
            prevEndDate={previousEndDate}
            prevStaffMemberFullName={event?.staffMember?.fullName}
            newStaffMemberFullName={newStaffMember?.fullName ?? ""}
            prevClinicName={prevClinicName}
            newClinicName={newClinicName}
            newStartDate={newStartDate}
            newEndDate={newEndDate}
            hasRoomConflict={hasRoomConflict}
            roomName={roomName}
            reservedSlotWarning={reservedSlotWarning}
          />
        )
      );

      const eventId = Number("modelId" in event ? event.modelId : event?.id);
      dispatch(
        setOnChange(() => {
          setAppointmentIdMutationInProgress(eventId);
          handleMovedEvent({
            ...droppedMeta,
            type: event.type,
            id: eventId,
            bookingClinicId: droppedMeta.clinicId ?? event.clinicId,
            timezone: event.clinic.timezone,
            prevStartDate: new Date(String(event.startDate)),
            room_start_moment: (event as Appointment).room_start_moment,
            room_end_moment: (event as Appointment).room_end_moment,
            ...(isClinicEvent(event)
              ? {
                  prevStaffMemberId: event.staffMemberId,
                  staffMemberIds: (event.staff_members ?? []).map((s) => s.id),
                  isGlobalPracticeEvent: event.isGlobalPracticeEvent
                }
              : {})
          });
        })
      );
      dispatch(
        setOnCancel(() => {
          setAppointmentIdMutationInProgress(undefined);
        })
      );
      dispatch(setConfirmChangeDialogOpen(true));
    },
    [
      handleMovedEvent,
      dispatch,
      selectedStaff,
      allClinics,
      isRoomAvailable,
      isDedicatedSlotEnforced,
      appointmentBlocks
    ]
  );

  const handleDroppedPersonalEvent = useCallback(
    (event: PersonalEvent, droppedMeta: any) => {
      const startDate = new Date(String(event?.startDate));

      const isDifferentClinic = !isSameClinicDrop(
        event.clinicId,
        droppedMeta.clinicId
      );

      if (!isDifferentClinic && isEqual(droppedMeta.startDate, startDate)) {
        return;
      }

      if (
        (!isAppointmentEditable(event) || startDate < new Date()) &&
        droppedMeta.startDate >= new Date()
      ) {
        handleCloneAppointment(event, droppedMeta);
      } else if (droppedMeta.startDate < new Date()) {
        dispatch(
          setCustomMessage(
            <WarningSnackbarContent title="Moving personal events to the past is currently restricted" />
          )
        );
        dispatch(setLabel("Appointment"));
        dispatch(setOpen(1000));
      } else {
        handleMoveEvent(event, droppedMeta);
      }
    },
    [handleCloneAppointment, handleMoveEvent, dispatch]
  );

  const handleDroppedAppointment = useCallback(
    (appointment: Appointment, droppedMeta: any) => {
      const startDate = new Date(String(appointment?.startDate));
      const endDate = new Date(String(appointment?.endDate));
      const appointmentFiniteStatus = !NON_FINAL_STATUSES_CODES.includes(
        appointment?.status
      );

      const isDifferentClinic = !isSameClinicDrop(
        appointment.clinicId,
        droppedMeta.clinicId
      );

      const isDifferentStaffMember =
        droppedMeta.staffMemberId !== appointment?.staffMemberId;

      if (
        !differenceInMinutes(droppedMeta.startDate, startDate) &&
        (isDifferentClinic || isDifferentStaffMember) &&
        droppedMeta.startDate >= new Date()
      ) {
        handleMoveEvent(appointment, droppedMeta);
        return;
      }

      if (
        !differenceInMinutes(droppedMeta.startDate, startDate) &&
        !differenceInMinutes(droppedMeta.endDate, endDate) &&
        !isDifferentClinic
      ) {
        return;
      }
      if (!isAppointmentEditable(appointment) && appointmentFiniteStatus) {
        handleCloneAppointment(appointment, droppedMeta);
      } else if (
        !differenceInMinutes(droppedMeta.startDate, startDate) &&
        differenceInMinutes(droppedMeta.endDate, endDate)
      ) {
        handleMoveEvent(appointment, droppedMeta);
      } else if (
        appointmentFiniteStatus &&
        droppedMeta.startDate < new Date()
      ) {
        dispatch(
          setCustomMessage(
            <WarningSnackbarContent title="Cloning appointments to the past is currently restricted" />
          )
        );
        dispatch(setLabel("Appointment"));
        dispatch(setOpen(1000));
      } else {
        handleMoveEvent(appointment, droppedMeta);
      }
    },
    [handleCloneAppointment, handleMoveEvent, dispatch]
  );

  const handleDroppedEvent = useCallback(
    (eventBeforeChange: Appointment | PersonalEvent, droppedMeta: any) => {
      /*
      droppedMeta doesn't contain type property, at the same time
       appointments and personalEvents could have similar id values,
       so we need pass eventBeforeChange object here
       */

      if (isAppointment(eventBeforeChange)) {
        handleDroppedAppointment(eventBeforeChange, droppedMeta);
      } else if (isAppointmentBlock(eventBeforeChange)) {
        handleDroppedPersonalEvent(eventBeforeChange, droppedMeta);
      } else if (isPersonalEvent(eventBeforeChange)) {
        handleDroppedPersonalEvent(eventBeforeChange, droppedMeta);
      } else if (isClinicEvent(eventBeforeChange)) {
        handleDroppedPersonalEvent(eventBeforeChange, droppedMeta);
      } else if (isPersonalWorkShiftEvent(eventBeforeChange)) {
        handleDroppedPersonalEvent(eventBeforeChange, droppedMeta);
      }
    },
    [handleDroppedAppointment, handleDroppedPersonalEvent]
  );

  const hasIntersectingEvents = useCallback(
    (
      startDate: Date | string,
      endDate: Date | string,
      staffMemberId: number | undefined
    ) => {
      if (
        !staffMemberId ||
        !startDate ||
        !endDate ||
        new Date(endDate) < new Date(startDate)
      )
        return false;

      let isOverlaps = Boolean(
        appointments.find(
          (appointment) =>
            appointment.staffMemberId === staffMemberId &&
            isWithinInterval(new Date(startDate), {
              start: new Date(appointment?.startDate),
              end: new Date(appointment?.endDate)
            }) &&
            !isEqual(new Date(appointment?.endDate), new Date(startDate))
        )
      );

      if (!isOverlaps) {
        isOverlaps = Boolean(
          personalEvents.find(
            (personalEvent) =>
              personalEvent.staffMemberId === staffMemberId &&
              isWithinInterval(new Date(startDate), {
                start: new Date(personalEvent?.startDate),
                end: new Date(personalEvent?.endDate)
              }) &&
              !isEqual(new Date(personalEvent?.endDate), new Date(startDate))
          )
        );
      }

      return isOverlaps;
    },
    [appointments, personalEvents]
  );

  const lastDraggedAppointment = React.useRef<any>();

  const updateAppointmentMetaData = useCallback(
    (appointmentId: number, updatedData: Partial<any>) => {
      if (appointmentMetaRef.current?.data?.id === appointmentId) {
        appointmentMetaRef.current = {
          ...appointmentMetaRef.current,
          data: {
            ...appointmentMetaRef.current.data,
            ...updatedData
          }
        };
      }
    },
    []
  );

  const appointmentTypesSet = useMemo(() => {
    return new Set(appointmentTypesValues);
  }, [appointmentTypesValues]);

  const filteredAppointments = useMemo(() => {
    if (appointmentTypesSet.size === 0) {
      return [...appointmentBlocks, ...appointments, ...personalEvents];
    }

    const filteredBlocks = appointmentBlocks.filter(
      ({ appointmentType }) =>
        appointmentType?.id && appointmentTypesSet.has(appointmentType.id)
    );

    const filteredAppointments = appointments.filter(({ appointmentType }) =>
      appointmentTypesSet.has(appointmentType?.id)
    );

    return [...filteredBlocks, ...filteredAppointments, ...personalEvents];
  }, [appointmentBlocks, appointmentTypesSet, appointments, personalEvents]);

  const positionedAppointments = useMemo(() => {
    if (filteredAppointments.length === 0) {
      return [];
    }

    const sortedAppointments =
      sortAppointmentsByStartDate(filteredAppointments);
    const zIndexMap = new Map<number | string, number>();

    return sortedAppointments.map((appointment, index) => {
      const overlappedAppointments = findOverlappedAppointments(
        sortedAppointments,
        index,
        appointment
      );
      const overlappedAppointment = overlappedAppointments.reduce(
        (maxZIndexAppointment, currentAppointment) => {
          const currentZIndex = zIndexMap.get(currentAppointment.id) || 0;
          const maxZIndex = zIndexMap.get(maxZIndexAppointment.id) || 0;
          return currentZIndex > maxZIndex
            ? currentAppointment
            : maxZIndexAppointment;
        },
        appointment
      );
      const zIndex = calculateZIndex(
        overlappedAppointment,
        appointment,
        zIndexMap
      );
      zIndexMap.set(appointment.id, zIndex);
      return {
        ...appointment,
        zIndex
      };
    });
  }, [filteredAppointments]);

  const safeRefetchEvents = useCallback(
    () => safeRefetch(refetch, isUninitialized),
    [refetch, isUninitialized]
  );

  const contextValue = useMemo(
    () => ({
      appointments: positionedAppointments,
      // ENG-2105: every loaded dedicated slot, before the appointment-type filter. The form's
      // reserved-slot check needs the slots the backend will see, not the ones on screen.
      allAppointmentBlocks: appointmentBlocks ?? [],
      setupAppointmentModal,
      removeAppointment,
      updateAppointmentStatus,
      queriesCounters: augmentedCounters,
      appointmentMetaRef,
      confirmationMessages,
      editingForm,
      setEditingForm,
      saveChangedAppointment: handleMovedEvent,
      handleDroppedEvent,
      appointmentIdMutationInProgress,
      setAppointmentIdMutationInProgress,
      isLoading,
      resetErrors,
      hasIntersectingEvents,
      lastDraggedAppointment,
      refetchEvents: safeRefetchEvents,
      isStaffMemberAvailable,
      personalWorkShift,
      appointmentFormId,
      updateAppointmentMetaData
    }),
    [
      appointmentBlocks,
      appointmentFormId,
      appointmentIdMutationInProgress,
      confirmationMessages,
      editingForm,
      handleDroppedEvent,
      handleMovedEvent,
      hasIntersectingEvents,
      isLoading,
      isStaffMemberAvailable,
      personalWorkShift,
      positionedAppointments,
      augmentedCounters,
      safeRefetchEvents,
      removeAppointment,
      resetErrors,
      setupAppointmentModal,
      updateAppointmentStatus,
      updateAppointmentMetaData
    ]
  );

  const handleOnCancel = useCallback(() => {
    if (onAppointmentCancel) {
      onAppointmentCancel();
    }
  }, [onAppointmentCancel]);

  const dragConflictStaffName = useMemo(() => {
    if (!dragConflict) return "This provider";
    const staff = (selectedStaff as Array<any>).find(
      (member) => member.id === dragConflict.staffMemberId
    );
    return (
      staff?.fullName ||
      `${staff?.first_name ?? ""} ${staff?.last_name ?? ""}`.trim() ||
      "This provider"
    );
  }, [dragConflict, selectedStaff]);

  const replayMove = useCallback(
    (start: Date, end: Date, allowOverlap: boolean) => {
      if (!dragConflict || dragReplayFiredRef.current) return;
      dragReplayFiredRef.current = true;
      setDragConflict(null);
      setAppointmentIdMutationInProgress(
        Number(dragConflict.modelId || dragConflict.id)
      );
      handleMovedEvent({
        ...dragConflict,
        startDate: start,
        endDate: end,
        allowOverlap
      });
    },
    [dragConflict, handleMovedEvent]
  );

  return (
    <AppointmentsContext.Provider value={contextValue}>
      <SelectAppointmentOutcomeProvider>
        <ScheduleNextAppointmentProvider
          setupAppointmentModal={setupAppointmentModal}
          appointmentFormId={appointmentFormId}
        >
          <UndeliveredDevicesProvider>
            <ReservedAppointmentTypesProvider>
              {children}
              {shouldMountAppointmentForm && (
                <DialogLoadErrorBoundary dialogName="appointment-form">
                  <Suspense fallback={<DialogLoadingFallback />}>
                    <AppointmentForm
                      formId={appointmentFormId}
                      onCancel={handleOnCancel}
                      onSave={handleCreateAppointment}
                      currentAppointment={currentAppointment}
                      errors={errors}
                      isRescheduling={isRescheduling}
                    />
                  </Suspense>
                </DialogLoadErrorBoundary>
              )}
              {dragConflict && (
                <ProviderConflictModal
                  staffMemberId={dragConflict.staffMemberId}
                  staffMemberName={dragConflictStaffName}
                  clinicId={dragConflict.bookingClinicId}
                  clinicTimezone={
                    allClinics?.find(
                      (clinic) => clinic.id === dragConflict.bookingClinicId
                    )?.timezone ?? dragConflict.timezone
                  }
                  attemptedStart={dragConflict.startDate}
                  attemptedEnd={dragConflict.endDate}
                  excludeAppointmentId={
                    dragConflict.modelId || dragConflict.id || undefined
                  }
                  // The save already failed, so the grid snapped the event back to
                  // its origin on its own — no refetch runs on the error path and the
                  // events data the scheduler renders from never changed. Dismissing
                  // the modal IS the revert; there is no drag state to unwind.
                  onGoBack={() => setDragConflict(null)}
                  onBookAnyway={() =>
                    replayMove(
                      dragConflict.startDate,
                      dragConflict.endDate,
                      true
                    )
                  }
                  onSlotPick={(start, end) => replayMove(start, end, false)}
                />
              )}
            </ReservedAppointmentTypesProvider>
          </UndeliveredDevicesProvider>
        </ScheduleNextAppointmentProvider>
      </SelectAppointmentOutcomeProvider>
    </AppointmentsContext.Provider>
  );
}

export default function useAppointments() {
  const appointmentsContext = React.useContext(AppointmentsContext);
  if (!appointmentsContext) {
    throw new Error(
      `useAppointments must be used within a AppointmentsProvider`
    );
  }
  return appointmentsContext;
}
